'use client'

import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'

const CHUNK_RELOAD_KEY = 'hf-chunk-reload-attempt'

// A ChunkLoadError / missing lazy chunk (404) means the browser is holding a
// stale build manifest from before the latest deploy. A one-time hard reload
// fetches the fresh manifest and self-heals instead of stranding the user on a
// dead page. Guard with sessionStorage so a genuinely-broken chunk can't loop.
function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.name ?? ''} ${error?.message ?? ''}`
  return /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to load chunk|Failed to fetch dynamically imported module/i.test(
    msg,
  )
}

export default function Error({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation()
  useEffect(() => {
    console.error(error)
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
          window.location.reload()
          return
        }
      } catch {
        /* private mode / storage disabled — fall through to manual retry */
      }
    } else {
      // Current build renders fine; clear the guard so a future stale-chunk
      // error can self-heal again.
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      } catch {
        /* ignore */
      }
    }
  }, [error])
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8">
      <AlertCircle className="h-12 w-12 text-destructive" />
      <h2 className="text-xl font-semibold">{t('errors.generic')}</h2>
      <p className="text-sm text-muted-foreground max-w-md text-center">{error.message}</p>
      <Button onClick={reset}>{t('common.tryAgain')}</Button>
    </div>
  )
}
